import dayjs, { Dayjs } from 'dayjs'

import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { IHoliday, ILocationHolidayCreatedEvent, ILocationHolidayUpdatedEvent } from '@vacationtracker/shared/types/holidays'

import { FIRST_PICKABLE_YEAR, HolidayLength, HolidayRow, isSameHoliday, overlappingHoliday, rowYear, utcToday } from '../location-holidays/logic'
import { DaySet, MIN_SPAN_DAYS, RecurringType, daySetOf, spanDays, specificDayIn } from '../location-holidays/recurrence'
import { IHolidaysByYear } from '../../types/locations'

const DAY = 'YYYY-MM-DD'

export const FIRST_PICKABLE_DAY = dayjs(`${FIRST_PICKABLE_YEAR}-01-01`)

// The year comes from UTC, as the backend's does, but the pickers compare local days.
const localDay = (today: Dayjs): Dayjs => dayjs(today.format(DAY))

export const lastPickableDay = (today: Dayjs = utcToday()): Dayjs => localDay(today).add(1, 'year').endOf('year')

/** Where an empty date picker opens: today's date in the year the Holidays tab showed, held to last year through next year. */
export const pickerOpeningDay = (tabYear: number | undefined, today: Dayjs = utcToday()): Dayjs => {
  const year = Math.min(Math.max(tabYear ?? today.year(), today.year() - 1), today.year() + 1)
  return localDay(today).year(year)
}

export interface HolidayFormValues {
  name: string
  length: HolidayLength
  /** YYYY-MM-DD, as picked. */
  startDate?: string
  endDate?: string
  recurring: boolean
  recurringType: RecurringType
  daySet: DaySet
  /** Days in a dynamic span; only sent for a multi-day holiday. */
  duration: number
  transfer: boolean
}

export type HolidayEventBody = Pick<
  ILocationHolidayCreatedEvent,
  'eventGroup' | 'locationId' | 'name' | 'startDate' | 'endDate' | 'recurring' | 'recurringRules' | 'transferIfFallsOnNonWorkingDay'
> & {
  eventType: ILocationHolidayCreatedEvent['eventType'] | ILocationHolidayUpdatedEvent['eventType']
  isHalfDay: boolean
  id?: ILocationHolidayUpdatedEvent['id']
}

export interface HolidayFormLocks {
  /** Nothing can change: the backend rejects updates to a repeating custom holiday. */
  whole: boolean
  /** Length and dates: an imported holiday follows the library. */
  dates: boolean
  /** Turning Repeat yearly on: imported holidays and holidays of past years. */
  repeatOn: boolean
  /** Recurrence type, rule and transfer: an imported holiday has none of its own. */
  repeatDetails: boolean
}

const NO_LOCKS: HolidayFormLocks = { whole: false, dates: false, repeatOn: false, repeatDetails: false }
const ALL_LOCKS: HolidayFormLocks = { whole: true, dates: true, repeatOn: true, repeatDetails: true }

/**
 * The holiday being edited is found by id. A holiday id that matches nothing locks the form, so it
 * can never post an update for a holiday it did not show. A row of no known type counts as custom.
 */
export function formLocks(existing: IHoliday | undefined, editing: boolean, today: Dayjs = utcToday()): HolidayFormLocks {
  if (!editing) return NO_LOCKS
  if (!existing) return ALL_LOCKS
  if (existing.type === 'LIB') return { whole: false, dates: true, repeatOn: true, repeatDetails: true }
  if (existing.recurring) return ALL_LOCKS
  return { ...NO_LOCKS, repeatOn: rowYear(existing) < today.year() }
}

export function findHoliday(holidays: IHolidaysByYear[], id?: string): IHoliday | undefined {
  if (!id) return undefined
  return holidays.flatMap((year) => year.holidays).find((holiday) => holiday.id === id)
}

export function initialFormValues(existing: IHoliday | undefined, holidays: IHolidaysByYear[]): HolidayFormValues {
  const group = existing?.multiDayId
    ? holidays
        .flatMap((year) => year.holidays)
        .filter((holiday) => holiday.multiDayId === existing.multiDayId)
        .map((holiday) => holidayDay(holiday.date))
        .sort()
    : []
  const startDate = group[0] ?? (existing ? holidayDay(existing.date) : undefined)
  const endDate = group.length ? group[group.length - 1] : undefined
  const rules = existing?.recurringRules
  return {
    name: existing?.name ?? '',
    length: existing?.multiDayId ? 'MULTI_DAY' : existing?.isHalfDay ? 'HALF_DAY' : 'SINGLE_DAY',
    startDate,
    endDate,
    recurring: Boolean(existing?.recurring),
    recurringType: rules?.specificDaySet ? 'SPECIFIC_DAY' : 'EXACT_DAY',
    daySet: rules?.specificDaySet ?? (startDate ? daySetOf(dayjs(startDate)) : [1, 1, 1]),
    duration: rules?.specificDayDuration ?? (startDate && endDate ? spanDays({ date: startDate, endDate }) : MIN_SPAN_DAYS),
    transfer: Boolean(existing?.transferIfFallsOnNonWorkingDay),
  }
}

export type HolidayFormErrors = Partial<Record<'name' | 'startDate' | 'endDate' | 'duration', string>>

const DATE_REQUIRED = 'components.addHolidayForm.date.error'

export function formErrors(values: HolidayFormValues): HolidayFormErrors {
  const multiDay = values.length === 'MULTI_DAY'
  const errors: HolidayFormErrors = {}
  if (!values.name) errors.name = 'form.nameRequired'
  if (!values.startDate) errors.startDate = DATE_REQUIRED
  if (multiDay && !values.endDate) errors.endDate = DATE_REQUIRED
  if (multiDay && values.startDate && values.endDate && values.endDate < values.startDate) errors.endDate = 'components.createHolidaysForm.createHolidayErrorEndDate'
  const dynamicSpan = values.recurring && values.recurringType === 'SPECIFIC_DAY' && multiDay
  if (dynamicSpan && !(Number.isInteger(values.duration) && values.duration >= MIN_SPAN_DAYS)) errors.duration = 'form.durationRequired'
  return errors
}

// A month-day in this year. A day this year's month lacks (Feb 29) is clamped rather than rolled into the next month.
const inYear = (today: Dayjs, day: string): Dayjs => {
  const month = dayjs(`${today.year()}-${day.slice(5, 7)}-01`)
  return month.date(Math.min(Number(day.slice(8, 10)), month.daysInMonth()))
}

export interface HolidayEventInput {
  locationId: string
  /** Validated by `formErrors`: the dates a submit needs are present. */
  values: HolidayFormValues
  existing?: IHoliday
  today?: Dayjs
}

/**
 * The shipped form's body. A repeating holiday is anchored to this year whatever year was picked:
 * a fixed date by its month and day, a dynamic one by its rule. An imported holiday has no rule of
 * its own, so it sends empty rules and no transfer flag, as the form always has.
 */
export function holidayEventBody({ locationId, values, existing, today = dayjs() }: HolidayEventInput): HolidayEventBody {
  const imported = existing?.type === 'LIB'
  const multiDay = values.length === 'MULTI_DAY'
  const picked = values.startDate as string
  let startDate = picked
  let endDate = multiDay ? (values.endDate as string) : picked
  let recurrence: Pick<HolidayEventBody, 'recurringRules' | 'transferIfFallsOnNonWorkingDay'> = {}

  if (values.recurring && imported) {
    recurrence = { recurringRules: {} }
  } else if (values.recurring && values.recurringType === 'SPECIFIC_DAY') {
    startDate = specificDayIn(today.year(), values.daySet).format(DAY)
    endDate = startDate
    recurrence = {
      recurringRules: { specificDaySet: values.daySet, ...(multiDay ? { specificDayDuration: values.duration } : {}) },
      transferIfFallsOnNonWorkingDay: values.transfer,
    }
  } else if (values.recurring) {
    const start = inYear(today, picked)
    let end = multiDay ? inYear(today, values.endDate as string) : start
    if (end.isBefore(start, 'day')) end = end.add(1, 'year')
    startDate = start.format(DAY)
    endDate = end.format(DAY)
    recurrence = { recurringRules: { exactDate: true }, transferIfFallsOnNonWorkingDay: values.transfer }
  }

  return {
    eventType: existing ? 'LOCATION_HOLIDAY_UPDATED' : 'LOCATION_HOLIDAY_CREATED',
    eventGroup: 'LOCATION',
    locationId,
    name: values.name,
    startDate,
    endDate,
    isHalfDay: values.length === 'HALF_DAY',
    recurring: values.recurring,
    ...recurrence,
    ...(existing ? { id: existing.id } : {}),
  }
}

export interface OverlapInput {
  body: HolidayEventBody
  existing?: IHoliday
  holidays: IHolidaysByYear[]
}

/**
 * Any other holiday on any day of the posted span; a dynamic span counts its whole duration. This
 * is stricter than the shipped form, which compared only the first and last day.
 */
/** Each day already holding a holiday on the location, with its names; the edited holiday's own days stay free. */
export function takenDays(holidays: IHolidaysByYear[], existing?: HolidayRow): Map<string, string[]> {
  const taken = new Map<string, string[]>()
  for (const row of holidays.flatMap((year) => year.holidays) as HolidayRow[]) {
    if (existing && isSameHoliday(existing, row)) continue
    const end = holidayDay(row.endDate ?? row.date)
    for (let day = dayjs(holidayDay(row.date)); day.format(DAY) <= end; day = day.add(1, 'day')) {
      const names = taken.get(day.format(DAY)) ?? []
      if (!names.includes(row.name)) taken.set(day.format(DAY), [...names, row.name])
    }
  }
  return taken
}

export function overlappingWith({ body, existing, holidays }: OverlapInput): HolidayRow | undefined {
  const duration = body.recurringRules?.specificDayDuration
  const endDate = duration
    ? dayjs(body.startDate)
        .add(duration - 1, 'day')
        .format(DAY)
    : body.endDate
  const self: HolidayRow = existing ?? { id: '', name: body.name, type: 'CUSTOM', date: body.startDate }
  return overlappingHoliday(
    self,
    { startDate: body.startDate, endDate },
    holidays.flatMap((year) => year.holidays)
  )
}
